import React from 'react';
import { observer } from 'mobx-react-lite';
import { Copy } from 'lucide-react';
import { useWrapRequest } from 'use-wrap-request';
import { SmartConnectOnboardingDto } from '@api/MobileClient';
import { Button } from '@components/ui/button';
import { Card, CardContent } from '@components/ui/card';
import { Api } from '@core/api';
import { toast } from '@core/toast';
import { injectTSDI } from '@core/tsdi';
import {
    WHITELABEL_PARAMETER,
    storedJoinUrl
} from '@src/onboarding/smart-connect/smart-connect-params';

interface SavedOnboardingsProps {
    baseUrl: string;
    reloadToken: number;
}

export const SavedOnboardings = observer(
    ({ baseUrl, reloadToken }: SavedOnboardingsProps) => {
        const { mobileClient } = injectTSDI(Api);

        const onboardings = useWrapRequest(
            async () => {
                const response =
                    await mobileClient.supportApi.smartConnectOnboardingSupportControllerList();
                return response.data;
            },
            { defaultData: [] as SmartConnectOnboardingDto[] }
        );

        React.useEffect(() => {
            onboardings.request([]);
        }, [reloadToken]);

        const copyLink = async (key: string) => {
            await navigator.clipboard.writeText(storedJoinUrl(baseUrl, key));
            toast.success('Link copied');
        };

        return (
            <Card className="mt-6">
                <CardContent>
                    <h2 className="mb-4 text-lg font-semibold tracking-tight">
                        Saved onboardings
                    </h2>
                    {onboardings.$.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            Nothing saved yet.
                        </p>
                    ) : (
                        <ul className="divide-y">
                            {onboardings.$.map((onboarding) => (
                                <li
                                    key={onboarding.key}
                                    className="flex items-center gap-4 py-2 text-sm"
                                >
                                    <span className="font-mono">
                                        {onboarding.key}
                                    </span>
                                    <span className="flex-1 truncate">
                                        {onboarding.name || 'Unnamed'}
                                    </span>
                                    <span className="text-muted-foreground">
                                        {whitelabelLabel(onboarding)}
                                    </span>
                                    <span className="text-muted-foreground">
                                        {onboarding.createdDate?.slice(0, 10)}
                                    </span>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                            copyLink(onboarding.key!)
                                        }
                                    >
                                        <Copy className="mr-2 h-4 w-4" />
                                        Copy link
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>
        );
    }
);

function whitelabelLabel(onboarding: SmartConnectOnboardingDto): string {
    const whitelabelId = onboarding.params?.[WHITELABEL_PARAMETER]?.[0];
    return whitelabelId ? `Community whitelabel: ${whitelabelId}` : '';
}
